import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { isDefined } from '@pai/commons'

import { CLIENT_CONFIG } from 'app/core/client-config/client-config.token'
import { Media, Portrait } from 'app/core/cms/generated/api-types'
import { bpMaxWidthMd, bpMaxWidthSm, bpMinWidthLg, bpMinWidthXl } from 'app/static/style-vars.const'
import { optimizedCmsImageUrl } from 'app/utils/cms-image-utils'
import { explicitComputed } from 'app/utils/signal-utils'

import { defaultObjectFit, normalizeRatio } from '../../dynamic-components/block-utils'

// todo loading blurry images (create them on upload in cms)

export type ImageWidth = `${number}${'vw' | 'px' | 'em' | 'rem'}`

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'picture[paiPicture]',
  templateUrl: './picture.component.html',
  styleUrls: ['./picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: {
    '[class.--loading]': 'loading()',
    '[class.--cover]': `mode() === 'cover'`,
    '[class.--contain]': `mode() === 'contain'`,
    '[style.aspect-ratio]': 'aspectRatio()',
  },
})
export class PictureComponent {
  static readonly IMAGE_QUALITY = 80

  /**
   * the sizes we provide to be loaded in the srcset
   * todo: make it dependent on the size(sm|md|lg|xl) inputs --> e.g. portraits are always (all screen sizes) shown with 128px square.
   *  therefore we do not need larger sizes in srcset. but should provide e.g 128x128, 256x256 and 384x384
   */
  static readonly SIZES = [/* largest first */ 3840, 2880, 1920, 1280, 960, 640, 320 /* smallest last */]

  private readonly assetsOrigin = inject(CLIENT_CONFIG).assetsOrigin

  readonly paiPicture = input.required<Media | Portrait | null>()
  readonly mode = input('cover', { transform: defaultObjectFit })
  readonly ratio = input(null, { transform: normalizeRatio })

  readonly priority = input(false, { transform: booleanAttribute })
  protected readonly aspectRatio = computed(() => {
    const ratio = this.ratio()
    const pic = this.paiPicture()
    if (ratio) {
      return ratio
    } else if (
      ratio === 'auto' &&
      pic &&
      isDefined(pic.width) &&
      isDefined(pic.height) &&
      pic.width > 0 &&
      pic.height > 0
    ) {
      return `${pic.width} / ${pic.height}`
    }
    return null
  })
  readonly size = input.required<ImageWidth>()
  readonly sizeSm = input<ImageWidth>()
  readonly sizeMd = input<ImageWidth>()
  readonly sizeLg = input<ImageWidth>()
  readonly sizeXl = input<ImageWidth>()

  readonly loaded = output<void>()

  protected readonly loading = signal(true)
  protected readonly fallbackSrc = explicitComputed(this.paiPicture, (pic) => this.generateFallbackSrc(pic))
  protected readonly imagePosition = computed(() => {
    const pic = this.paiPicture()
    const fit = this.mode()
    return pic && fit === 'cover' ? this.getImagePosition(pic) : null
  })
  protected readonly sizesAttr = explicitComputed(
    [this.size, this.sizeSm, this.sizeMd, this.sizeLg, this.sizeXl],
    this.generateSizes.bind(this),
  )
  protected readonly defaultSrcSet = explicitComputed(this.paiPicture, this.generateSrcSet.bind(this, 'jpeg'))
  protected readonly webpSrcSet = explicitComputed(this.paiPicture, this.generateSrcSet.bind(this, 'webp'))

  protected onLoad() {
    this.loading.set(false)
    this.loaded.emit()
  }

  private getImagePosition({ focalX, focalY }: Media | Portrait): string | null {
    return isDefined(focalX) && isDefined(focalY) && !(focalX === 50 && focalY === 50) ? `${focalX}% ${focalY}%` : null
  }

  private generateFallbackSrc(pic: Media | Portrait | null): string | null {
    if (!pic) {
      return null
    }
    const width = Math.min(Number(pic.width), PictureComponent.SIZES[0] /* largest size */)

    return optimizedCmsImageUrl(this.assetsOrigin, pic, width, 0, PictureComponent.IMAGE_QUALITY, 'jpeg')
  }

  private generateSrcSet(format: 'webp' | 'jpeg', pic: Media | Portrait | null): string | null {
    if (!pic) {
      return null
    }
    const origWidth = Number(pic.width)
    const origHeight = Number(pic.height)

    const fromWidths = PictureComponent.SIZES
      // do not generate sizes larger than the original
      .filter((w) => w < origWidth)
      .map((w) => `${optimizedCmsImageUrl(this.assetsOrigin, pic, w, 0, PictureComponent.IMAGE_QUALITY, format)} ${w}w`)
      .reverse()

    // if original is smaller than one of the sizes, we add the original as well
    const originalSize = PictureComponent.SIZES.some((w) => w > origWidth)
      ? [
          `${optimizedCmsImageUrl(this.assetsOrigin, pic, origWidth, origHeight, PictureComponent.IMAGE_QUALITY, format)} ${origWidth}w`,
        ]
      : []
    return [...originalSize, ...fromWidths].join(', ')
  }

  private generateSizes(std: ImageWidth, sm?: ImageWidth, md?: ImageWidth, lg?: ImageWidth, xl?: ImageWidth): string {
    // the first matching query is used. therefore the order matters
    return [
      sm && `(max-width: ${bpMaxWidthSm}px) ${sm}`,
      md && `(max-width: ${bpMaxWidthMd}px) ${md}`,
      xl && `(min-width: ${bpMinWidthXl}px) ${xl}`,
      lg && `(min-width: ${bpMinWidthLg}px) ${lg}`,
      std,
    ]
      .filter(isDefined)
      .join(', ')
  }
}
